import {
  fetchWithAuth,
  REGISTRATIONS_API,
  SEASON_EVENTS_USER_API,
  PRICE_SETTINGS_API,
} from "../../utils/api";
import { enhanceEventWithPaymentSettings } from "../../utils/stripeApi";

/**
 * Safe stringify utility to handle circular references
 */
export const safeStringify = (obj) => {
  // For synthetic React events, extract only necessary properties
  if (obj && obj.nativeEvent && obj._reactName) {
    return JSON.stringify({
      eventType: obj._reactName,
      timestamp: new Date().toISOString()
    });
  }
  
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    // Skip browser DOM elements and React internal props
    if (
      key.startsWith('__react') || 
      key === 'stateNode' || 
      key === 'window' ||
      value instanceof HTMLElement || 
      value === window || 
      (typeof value === 'function')
    ) {
      return '[Omitted]';
    }
    
    // Handle circular references
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  });
};

/**
 * Format date range for display
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "N/A";

  const createLocalDate = (dateStr) => {
    const date = new Date(dateStr);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  };

  const start = createLocalDate(startDate);
  const end = createLocalDate(endDate);
  const options = { month: "long", day: "numeric", year: "numeric" };

  // If same day
  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString(undefined, options);
  }

  // Different days but same month and year
  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    })} ${start.getDate()}-${end.getDate()}`;
  }

  // Different months or years
  return `${start.toLocaleDateString(
    undefined,
    options
  )} - ${end.toLocaleDateString(undefined, options)}`;
};

/**
 * Fetch event details and settings
 */
export const fetchEventAndSettings = async (eventId) => {
  try {
    if (!eventId) {
      throw new Error("No event ID provided.");
    }

    // Fetch events list
    const allEventsData = await fetchWithAuth(SEASON_EVENTS_USER_API);

    if (!allEventsData || !Array.isArray(allEventsData.events)) {
      throw new Error("Failed to fetch or parse event list.");
    }

    // Find specific event by ID or slug
    const targetEventId = eventId;
    const apiEvent = allEventsData.events.find(event => 
      event.id === targetEventId || 
      event.eventId === targetEventId ||
      (event.name && event.name.replace(/\s+/g, '-').toLowerCase() === targetEventId)
    );

    if (!apiEvent) {
      throw new Error(`Event with ID ${eventId} not found`);
    }

    // Verify event has owner/organizer
    if (!apiEvent.conventionOwnerId && !apiEvent.createdBy && 
        !apiEvent.ownerId && !apiEvent.organizerId) {
      throw new Error("This event has no owner/organizer assigned and cannot accept payments");
    }

    // Store the creator's/organizer's ID
    apiEvent.conventionOwnerId = 
      apiEvent.conventionOwnerId || apiEvent.createdBy || 
      apiEvent.ownerId || apiEvent.organizerId;

    // Find event type info
    const eventTypeInfo = allEventsData.eventTypes.find(type => type.name === apiEvent.eventType);
    
    // Determine organizer ID for settings fetch
    const organizerId = apiEvent.conventionOwnerId;
    if (!organizerId) {
      throw new Error("Event owner/organizer ID could not be determined.");
    }

    // Fetch pricing and criteria settings
    const baseUrl = PRICE_SETTINGS_API.substring(0, PRICE_SETTINGS_API.lastIndexOf('/'));
    const correctCriteriaUrl = `${baseUrl}/registration-rules?organizerId=${organizerId}`;
    
    const [fetchedPricingSettings, fetchedCriteriaSettings] = await Promise.all([
      fetchWithAuth(`${PRICE_SETTINGS_API}?organizerId=${organizerId}`)
        .catch(() => ({})),
      fetchWithAuth(correctCriteriaUrl)
        .catch(() => ({}))
    ]);

    // Format pricing settings
    const formattedPricingSettings = {
      ...fetchedPricingSettings,
      competitionPricing: fetchedPricingSettings?.competitionPricing || 
                        fetchedPricingSettings?.pricing?.competitionPricing || 
                        fetchedPricingSettings?.settings?.competitionPricing || [],
      registrationPricing: fetchedPricingSettings?.registrationPricing || 
                         fetchedPricingSettings?.pricing?.registrationPricing || 
                         fetchedPricingSettings?.settings?.registrationPricing || []
    };

    // Format style categories
    let styleCategories = fetchedCriteriaSettings?.styleCategories || 
                       fetchedCriteriaSettings?.criteria?.styleCategories || 
                       fetchedCriteriaSettings?.settings?.styleCategories || [];
    
    styleCategories = styleCategories.map(style => {
      if (typeof style === 'string') {
        return { label: style, value: style };
      } else if (typeof style === 'object') {
        return { 
          label: style.label || style.name || style.value || style.id || style.style || '', 
          value: style.value || style.id || style.label || style.name || style.style || ''
        };
      }
      return style;
    });

    // Format criteria settings
    const formattedCriteriaSettings = {
      ...fetchedCriteriaSettings,
      styleCategories: styleCategories,
      ageGroups: fetchedCriteriaSettings?.ageGroups || 
                fetchedCriteriaSettings?.criteria?.ageGroups || 
                fetchedCriteriaSettings?.settings?.ageGroups || []
    };

    // Enhance event with payment settings
    const enhancedEvent = await enhanceEventWithPaymentSettings(apiEvent);

    // Final event formatting
    const formattedEvent = {
      id: apiEvent.id || apiEvent.eventId || apiEvent.name?.replace(/\s+/g, '-').toLowerCase(),
      name: apiEvent.name || `${apiEvent.city} Event`,
      startDate: apiEvent.startDate,
      endDate: apiEvent.endDate,
      date: formatDateRange(apiEvent.startDate, apiEvent.endDate),
      location: apiEvent.city,
      type: [
        eventTypeInfo?.hasConvention && "Convention",
        eventTypeInfo?.hasCompetition && "Competition",
      ].filter(Boolean).join(' + ') || eventTypeInfo?.name || 'Event',
      registrationStatus: apiEvent.status,
      hasCompetition: apiEvent.hasCompetition,
      hasConvention: apiEvent.hasConvention,
      hasObserverPasses: apiEvent.hasObserverPasses !== false,
      hasTeachers: apiEvent.hasTeachers !== false,
      registered: false,
      venue: {
        name: apiEvent.venue,
        address: `${apiEvent.venue}, ${apiEvent.city}`,
      },
      pricing: enhancedEvent.pricing || {
        dancer: {},
        teacher: {},
        observer: {},
        routine: {},
        workshopLevels: [],
        competitionPricing: []
      },
      registrationSettings: enhancedEvent.registrationSettings || {
        ageDivisions: [],
        routineCategories: [],
      },
      allowDeposit: enhancedEvent.allowDeposit || false,
      depositAmount: enhancedEvent.depositAmount || 0,
      balanceDueDate: apiEvent.startDate,
      conventionOwnerId: organizerId,
      rules: enhancedEvent.rules || {
        competition: []
      },
      paymentSettings: enhancedEvent.paymentSettings || {},
      eventTypeInfo: eventTypeInfo || {},
    };

    return {
      event: formattedEvent,
      pricingSettings: formattedPricingSettings,
      criteriaSettings: formattedCriteriaSettings,
    };
  } catch (err) {
    throw new Error(err.message || "Failed to load event data or required settings");
  }
};

/**
 * Update registration on the server
 */
export const updateRegistrationDirectly = async (
  registrationId,
  stepId,
  userId,
  eventId,
  data,
  isStepComplete
) => {
  try {
    // Validate registrationId
    if (!registrationId || typeof registrationId !== 'string') {
      return null;
    }
    
    // Validate essential fields
    if (!userId || !eventId) {
      return null;
    }

    // Construct the payload
    const updatePayload = {
      userId, 
      eventId,
      ...data,
      ...(data.competition && {
        competition: {
          ...data.competition
        }
      })
    };

    // Send the update
    return await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}`,
      {
        method: "PUT",
        body: updatePayload,
      }
    );
  } catch (err) {
    console.error("Error updating registration:", err);
    throw err;
  }
};

/**
 * Load registration routines
 */
export const loadRegistrationRoutines = async (registrationId) => {
  if (!registrationId) return null;
  
  try {
    const routinesResponse = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/routines`
    );
    
    if (routinesResponse && routinesResponse.routines) {
      return routinesResponse.routines;
    }
    return [];
  } catch (error) {
    console.error('Error loading routines:', error);
    throw error;
  }
};

/**
 * Deduplicate and hydrate registration data
 */
export const deduplicateAndHydrateRegistrationData = async (registrationId) => {
  if (!registrationId) return null;
  
  try {
    // Get main registration data
    const regResponse = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}`);
    
    // Get all related routines
    const routinesResponse = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}/routines`);
    
    if (regResponse) {
      // Process registration data
      let dancersMap = new Map();
      
      // Process dancers from main registration
      if (Array.isArray(regResponse.registrationDancers)) {
        regResponse.registrationDancers.forEach(dancer => {
          if (dancer && dancer.dancerId) {
            dancersMap.set(dancer.dancerId, {
              ...dancer,
              workshopLevel: null,
              options: {
                ...(dancer.options || {}),
                workshopLevel: null
              }
            });
          }
        });
      }
      
      // Process dancers from routines
      const routines = routinesResponse?.routines || [];
      const dancersFromRoutines = [];
      
      routines.forEach(routine => {
        const routineTitle = routine.title || 'Unknown Routine';
        // Create a more robust way to extract dancer IDs from the routine's dancers array
        let dancerIds = [];
        
        // Handle different formats of the dancers array
        if (Array.isArray(routine.dancers)) {
          // First, try to extract dancer IDs based on format
          dancerIds = routine.dancers.map(d => {
            // If it's already a string, use it
            if (typeof d === 'string') return d;
            
            // If it's a DynamoDB object with 'S' property
            if (typeof d === 'object' && d.S) return d.S;
            
            // If it's some other format, try to extract it
            return d.dancerId || d.id || '';
          }).filter(Boolean); // Remove empty values
        }
        
        console.log(`Extracted ${dancerIds.length} dancer IDs from routine "${routineTitle}": `, dancerIds);
        
        // Create dancer objects for each dancer ID
        dancerIds.forEach(dancerId => {
          if (!dancerId) return;
          
          // Check if this dancer is already in the array
          const existingIndex = dancersFromRoutines.findIndex(d => d.dancerId === dancerId);
          if (existingIndex !== -1) {
            // If already exists, just update the routine reference
            dancersFromRoutines[existingIndex].routineNames = 
              dancersFromRoutines[existingIndex].routineNames || [];
            
            if (!dancersFromRoutines[existingIndex].routineNames.includes(routineTitle)) {
              dancersFromRoutines[existingIndex].routineNames.push(routineTitle);
            }
          } else {
            // Add new dancer
            dancersFromRoutines.push({
              dancerId,
              fromRoutine: routineTitle,
              routineName: routineTitle,
              routineNames: [routineTitle],
              isFromRoutine: true,
              autoAdded: true
            });
          }
        });
      });
      
      console.log(`Total dancers from routines: ${dancersFromRoutines.length}`);
      
      // Return processed data
      return {
        ...regResponse,
        routines: routines,
        dancerIds: [...dancersMap.keys()],
        registrationDancers: [...dancersMap.values()],
        dancersFromRoutines: dancersFromRoutines
      };
    }
    return null;
  } catch (error) {
    console.error("Error deduplicating registration data:", error);
    throw error;
  }
};

/**
 * Load registration data
 */
export const loadRegistrationData = async (registrationId) => {
  if (!registrationId) return null;
  
  try {
    // Get the registration data
    const regResponse = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}`);
    
    if (regResponse) {
      // Get routines
      const routines = await loadRegistrationRoutines(registrationId);
      
      // Return combined data
      return {
        ...regResponse,
        routines: routines || []
      };
    }
    return null;
  } catch (error) {
    console.error('Error loading registration data:', error);
    throw error;
  }
};

/**
 * Check for existing registration
 */
export const checkExistingRegistration = async (eventId, userId) => {
  try {
    // Query the API for existing registration
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}?eventId=${eventId}&userId=${userId}`
    );

    if (!response) {
      throw new Error("Failed to check for existing registration");
    }

    if (response.registrations?.length > 0) {
      // Sort by updatedAt to get the most recent registration
      const sortedRegistrations = response.registrations.sort(
        (a, b) =>
          new Date(b.updatedAt || b.createdAt) -
          new Date(a.updatedAt || a.createdAt)
      );

      return sortedRegistrations[0];
    }
    
    return null;
  } catch (err) {
    console.error("Error checking registration:", err);
    throw err;
  }
};

/**
 * Create a new registration
 */
export const createNewRegistration = async (eventId, userId, eventData) => {
  try {
    // Validate essential fields
    if (!userId || !eventId) {
      console.error('Missing required fields for registration creation:', { userId, eventId });
      throw new Error("Missing userId or eventId");
    }

    console.log('=== REGISTRATION SERVICE: Creating registration ===');
    console.log('Event ID:', eventId);
    console.log('User ID:', userId);
    console.log('Event data:', eventData);

    // First check if registration already exists
    console.log('Checking for existing registration...');
    const existingReg = await checkExistingRegistration(eventId, userId);
    if (existingReg) {
      console.log('Found existing registration, returning it:', existingReg.registrationId);
      return existingReg;
    }
    console.log('No existing registration found, proceeding with creation...');

    // Initial registration data
    const initialData = {
      eventId,
      userId,
      currentStep: 0,
      currentStepPath: 'overview',
      lastCompletedStep: null,
      status: "in-progress",
      eventDetails: {
        name: eventData.name,
        startDate: eventData.startDate,
        endDate: eventData.endDate,
        location: eventData.location,
        venue: eventData.venue?.name || '',
      },
      dancerIds: [],
      teacherIds: [],
      observerIds: [],
      registrationDancers: [],
      registrationTeachers: [],
      registrationObservers: [],
      competition: {
        selectedRoutineIds: [],
        mappings: {},
        routineOptions: {}
      },
      pricing: {
        subtotal: 0,
        total: 0,
        discounts: [],
      },
      createdAt: new Date().toISOString(),
    };

    console.log('Creating new registration with data:', JSON.stringify(initialData, null, 2));

    // Create the registration
    console.log('Calling API endpoint:', REGISTRATIONS_API);
    console.log('Request method: POST');
    console.log('Request body:', JSON.stringify(initialData, null, 2));
    
    const response = await fetchWithAuth(REGISTRATIONS_API, {
      method: "POST",
      body: safeStringify(initialData),
    });

    console.log('=== API RESPONSE RECEIVED ===');
    console.log('Response status: SUCCESS');
    console.log('Response data:', JSON.stringify(response, null, 2));

    if (!response) {
      console.error('No response received from registration API');
      throw new Error("No response received from registration API");
    }

    if (response && response.registrationId) {
      console.log('=== REGISTRATION CREATED SUCCESSFULLY ===');
      console.log('Registration ID:', response.registrationId);
      console.log('Registration status:', response.status);
      console.log('Registration data:', response);
      return response;
    } else {
      console.error('=== INVALID RESPONSE FORMAT ===');
      console.error('Expected registrationId but got:', response);
      throw new Error("Failed to create registration - invalid response format");
    }
  } catch (err) {
    console.error('=== REGISTRATION CREATION ERROR ===');
    console.error('Error type:', err.constructor.name);
    console.error('Error message:', err.message);
    console.error('Error status:', err.status);
    console.error('Error response data:', err.responseData);
    console.error('Full error object:', err);

    // Handle conflict error
    if (err.status === 409 || (err.responseData && err.responseData.error === "Registration already exists")) {
      console.log('Registration already exists, fetching existing registration');
      try {
        const existingReg = await checkExistingRegistration(eventId, userId);
        if (existingReg) {
          console.log('Successfully retrieved existing registration:', existingReg.registrationId);
          return existingReg;
        } else {
          console.error('Conflict error but could not retrieve existing registration');
          throw new Error("Registration conflict - unable to retrieve existing registration");
        }
      } catch (fetchError) {
        console.error('Error fetching existing registration after conflict:', fetchError);
        throw new Error("Registration conflict - unable to retrieve existing registration");
      }
    }

    // Handle authentication errors
    if (err.status === 401 || err.status === 403) {
      console.error('Authentication error when creating registration:', err);
      throw new Error("Authentication failed. Please try logging in again.");
    }

    // Handle network errors
    if (err.name === 'NetworkError' || err.message.includes('fetch')) {
      console.error('Network error when creating registration:', err);
      throw new Error("Network error. Please check your connection and try again.");
    }

    // Re-throw the original error with additional context
    throw new Error(`Registration creation failed: ${err.message}`);
  }
};

/**
 * Participant API functions
 */

export const fetchRegistrationParticipants = async (registrationId) => {
  if (!registrationId) return null;
  
  try {
    const participantsResponse = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/participants`
    );
    
    if (participantsResponse && participantsResponse.participants) {
      // Process and normalize participants
      const normalizeParticipant = (participant) => {
        const normalized = {...participant};
        
        // Handle workshopLevel field
        if (normalized.workshopLevel && typeof normalized.workshopLevel === 'object' && normalized.workshopLevel.S) {
          normalized.workshopLevel = normalized.workshopLevel.S;
        }
        
        // Normalize options object
        if (normalized.options) {
          const normalizedOptions = {...normalized.options};
          
          if (normalizedOptions.workshopLevel && typeof normalizedOptions.workshopLevel === 'object' && normalizedOptions.workshopLevel.S) {
            normalizedOptions.workshopLevel = normalizedOptions.workshopLevel.S;
          }
          
          // Handle other fields in DynamoDB format
          Object.keys(normalizedOptions).forEach(key => {
            if (typeof normalizedOptions[key] === 'object') {
              if ('BOOL' in normalizedOptions[key]) {
                normalizedOptions[key] = normalizedOptions[key].BOOL;
              } else if ('S' in normalizedOptions[key]) {
                normalizedOptions[key] = normalizedOptions[key].S;
              } else if ('N' in normalizedOptions[key]) {
                normalizedOptions[key] = Number(normalizedOptions[key].N);
              }
            }
          });
          
          normalized.options = normalizedOptions;
        }
        
        // Ensure workshopLevel is consistent
        if (normalized.workshopLevel && (!normalized.options || !normalized.options.workshopLevel)) {
          if (!normalized.options) normalized.options = {};
          normalized.options.workshopLevel = normalized.workshopLevel;
        } else if (!normalized.workshopLevel && normalized.options && normalized.options.workshopLevel) {
          normalized.workshopLevel = normalized.options.workshopLevel;
        }
        
        return normalized;
      };
      
      // Group participants by type
      const dancers = participantsResponse.participants
        .filter(p => p.participantType === 'dancer')
        .map(normalizeParticipant);
      
      const teachers = participantsResponse.participants
        .filter(p => p.participantType === 'teacher')
        .map(normalizeParticipant);
      
      const observers = participantsResponse.participants
        .filter(p => p.participantType === 'observer')
        .map(normalizeParticipant);
      
      return {
        dancers,
        teachers,
        observers
      };
    }
    return {
      dancers: [],
      teachers: [],
      observers: []
    };
  } catch (error) {
    console.error('Error loading participants:', error);
    throw error;
  }
};

export const addParticipantToRegistration = async (registrationId, participant, type) => {
  if (!registrationId) return null;
  
  try {
    // Handle missing required fields
    if (!participant.dateOfBirth && !participant.birthDate) {
      console.warn("Participant missing date of birth:", participant);
      // Add a default birthdate for dancers from routines
      if (participant.isFromRoutine) {
        participant.dateOfBirth = "2000-01-01"; // Use a default date
      }
    }
    
    // Create a unique identifier
    const uniqueIdentifier = `${participant.firstName}-${participant.lastName}-${participant.dateOfBirth || participant.birthDate || 'unknown'}-${participant.routineName || ''}-${participant.originalDancerId || participant.dancerId}`;
    
    // Clean up the participant data
    const cleanedParticipant = {
      ...participant,
      dancerId: participant.dancerId,
      firstName: participant.firstName || "Unknown",
      lastName: participant.lastName || "Dancer",
      dateOfBirth: participant.dateOfBirth || participant.birthDate || "2000-01-01",
      isFromRoutine: participant.isFromRoutine || !!participant.fromRoutine,
      fromRoutine: participant.fromRoutine || participant.routineName,
      workshopLevel: participant.workshopLevel || participant.options?.workshopLevel,
      options: {
        ...(participant.options || {}),
        competition: participant.isFromRoutine ? true : participant.options?.competition || false,
        workshopLevel: participant.workshopLevel || participant.options?.workshopLevel
      }
    };
    
    console.log("Adding participant with cleaned data:", cleanedParticipant);
    
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/participant`,
      {
        method: 'POST',
        body: safeStringify({ 
          participant: {
            ...cleanedParticipant,
            uniqueIdentifier,
            originalDancerId: participant.originalDancerId || participant.dancerId,
            registrationContext: {
              addedAt: new Date().toISOString(),
              addedFrom: participant.fromRoutine ? 'routine' : 'manual',
              routineId: participant.routineId || null,
              routineName: participant.routineName || null
            }
          }, 
          type 
        })
      }
    );
    
    if (response && response.participant) {
      return response.participant.participantId || response.participant;
    }
    
    if (response && response.participantId) {
      return response.participantId;
    }
    
    return null;
  } catch (error) {
    console.error(`Error adding ${type}:`, error);
    throw error;
  }
};

export const updateRegistrationParticipant = async (registrationId, participantId, updateData) => {
  if (!registrationId) return null;
  
  try {
    // Ensure workshopLevel is consistent
    const normalizedUpdateData = {...updateData};
    
    if (normalizedUpdateData.workshopLevel) {
      if (!normalizedUpdateData.options) normalizedUpdateData.options = {};
      normalizedUpdateData.options.workshopLevel = normalizedUpdateData.workshopLevel;
    } else if (normalizedUpdateData.options && normalizedUpdateData.options.workshopLevel) {
      normalizedUpdateData.workshopLevel = normalizedUpdateData.options.workshopLevel;
    }
    
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/participants/${participantId}`,
      {
        method: 'PUT',
        body: safeStringify(normalizedUpdateData)
      }
    );
    
    if (response) {
      return response;
    }
    return null;
  } catch (error) {
    console.error("Error updating participant:", error);
    throw error;
  }
};

export const removeParticipantFromRegistration = async (registrationId, participantId) => {
  if (!registrationId) return false;
  
  try {
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/participants/${participantId}`,
      {
        method: 'DELETE'
      }
    );
    
    if (response) {
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error removing participant:', error);
    throw error;
  }
};

/**
 * Routine API functions
 */

export const addRoutineToRegistration = async (registrationId, routineId, options = {}) => {
  if (!registrationId) return null;
  
  try {
    const payload = { 
      routineId,
      style: options.style || '',
      type: options.type || '',
      typeLabel: options.typeLabel || '',
      averageAge: options.averageAge || null,
      ageGroup: options.ageGroup || ''
    };
    
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/routine`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
    
    if (response && response.routineCloneId) {
      return response.routineCloneId;
    }
    return null;
  } catch (error) {
    console.error('Error adding routine:', error);
    throw error;
  }
};

export const updateRegistrationRoutine = async (registrationId, routineCloneId, updateData) => {
  if (!registrationId || !routineCloneId || routineCloneId === "undefined") {
    return null;
  }
  
  try {
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/routines/${routineCloneId}`,
      {
        method: 'PUT',
        body: safeStringify(updateData)
      }
    );
    
    if (response) {
      return response;
    }
    return null;
  } catch (error) {
    console.error("Error updating routine:", error);
    throw error;
  }
};

export const removeRoutineFromRegistration = async (registrationId, routineCloneId) => {
  if (!registrationId) return false;
  
  try {
    const response = await fetchWithAuth(
      `${REGISTRATIONS_API}/${registrationId}/routines/${routineCloneId}`,
      {
        method: 'DELETE'
      }
    );
    
    if (response) {
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error removing routine:', error);
    throw error;
  }
}; 